import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeModeProvider } from "./theme/ThemeModeProvider";
import { warmUpBlog } from "./services/blogCache";
import { AuthProvider } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import { ScrollToTop } from "./components/ScrollToTop";

import { HomePage } from "./pages/HomePage";
import { AgentsPage } from "./pages/AgentsPage";
import { PlatformsPage } from "./pages/PlatformsPage";
import { PlatformDetailPage } from "./pages/PlatformDetailPage";
import { PricingPage } from "./pages/PricingPage";
import { TrustPage } from "./pages/TrustPage";
import { HonestyPage } from "./pages/HonestyPage";
import { ComsPage } from "./pages/ComsPage";
import { DemoPage } from "./pages/DemoPage";
import { OperatorsPage } from "./pages/OperatorsPage";
import { InstallPage } from "./pages/InstallPage";
import { SecurityPage } from "./pages/SecurityPage";
import { DurabilityPage } from "./pages/DurabilityPage";
import { LegalPage } from "./pages/LegalPage";
import { DocsPage } from "./pages/DocsPage";
import { SupportPage } from "./pages/SupportPage";
import { AgentReadyPage } from "./pages/AgentReadyPage";
import { AskYourAgentPage } from "./pages/AskYourAgentPage";
import { StartPage } from "./pages/StartPage";
import { PromptsPage } from "./pages/PromptsPage";
import { ThemesPage } from "./pages/ThemesPage";
import { InvestorsPage } from "./pages/InvestorsPage";
import { ZenPage } from "./pages/ZenPage";
import { PayPage } from "./pages/PayPage";
import { NotFoundPage } from "./pages/NotFoundPage";

import { BlogListPage } from "./pages/blog/BlogListPage";
import { BlogPostPage } from "./pages/blog/BlogPostPage";
import { BlogSeriesPage } from "./pages/blog/BlogSeriesPage";

// CO 321 — the human auth surface (/login, /register, OAuth callback) and the
// thin human manager app (/manager/*) were removed. The front door is now
// agent-native: a human authorizes an agent at the doorway (OAuth) and the
// agent self-admits as a manager root via federation_apply (CO 263) and builds
// tenants. See docs/noise/changes/2026-07-20-321-...  The shared
// @comai-portal/auth-ui package and the backend /managers/* endpoints are
// unchanged; only the portal-app human UI is gone. AuthProvider stays mounted
// (inert without a token) so context imports elsewhere keep resolving.

function App() {
  // Warm up the admin-controlled blog API on init to kill cold-start lag on /blog.
  useEffect(() => {
    warmUpBlog();
  }, []);

  return (
    <ThemeModeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Shell-wrapped marketing + content routes */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              {/* /why retired (CO 069) — its job is done by Home + /operators */}
              <Route
                path="/why"
                element={<Navigate to="/operators" replace />}
              />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/platforms" element={<PlatformsPage />} />
              <Route path="/platforms/:slug" element={<PlatformDetailPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/coms" element={<ComsPage />} />
              <Route path="/operators" element={<OperatorsPage />} />
              {/* CO 494 Phase 4 (Ron 2026-09-06): the distribution door — the
                  portal's purpose is growing the network by node installs. */}
              <Route path="/install" element={<InstallPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/trust" element={<TrustPage />} />
              <Route path="/honesty" element={<HonestyPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/durability" element={<DurabilityPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/support" element={<SupportPage />} />
              {/* CO 530 — the prompt library page, generated from the
                  federation-comos-profiles canon; statuses render exactly
                  as the canon records them (proven / untested / gate-held). */}
              <Route path="/prompts" element={<PromptsPage />} />
              {/* CO 680 Phase 2b — the theme library page: swatches generated
                  from the house supply, standing read live from
                  rendering_theme_library and never built. */}
              <Route path="/themes" element={<ThemesPage />} />

              {/* CO 237: Agent Readiness Check — primary public CTA, re-homed
                  from comai-portal gateway-app. Stateless inline-result scan. */}
              <Route path="/agent-ready" element={<AgentReadyPage />} />
              {/* CO 257: the human-side routing CTA — one sentence the human
                  gives their agent; the agent evaluates the live disclosure
                  surface and reports back. */}
              <Route path="/ask-your-agent" element={<AskYourAgentPage />} />
              {/* CO 321 — the "Start free" action door: leads directly with the
                  become-a-manager prompt (evaluate-first lives on /ask-your-agent). */}
              <Route path="/start" element={<StartPage />} />
              {/* CO 458 — the pay-a-quote page: one offer (<tenant>~<product>),
                  one price, one button, over the existing retail USD rail. */}
              <Route path="/pay/:offer" element={<PayPage />} />

              {/* Legal — rendered from canonical markdown (no MUI) */}
              <Route path="/privacy" element={<LegalPage slug="privacy" />} />
              <Route path="/terms" element={<LegalPage slug="terms" />} />
              <Route
                path="/acceptable-use"
                element={<LegalPage slug="acceptable-use" />}
              />
              <Route path="/cookies" element={<LegalPage slug="cookies" />} />
              <Route path="/dpa" element={<LegalPage slug="dpa" />} />
              <Route path="/blog" element={<BlogListPage />} />
              {/* CO 540 — a series folder; declared before /blog/:slug so
                  "series" is never read as a post slug. */}
              <Route path="/blog/series/:key" element={<BlogSeriesPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />

              {/* Investor docs — unlisted, noindex, link-shared (CO 297-003) */}
              <Route path="/investors" element={<InvestorsPage />} />
              <Route path="/investors/:slug" element={<InvestorsPage />} />

              {/* CO 321 — the human auth surface (/login, /register, callbacks)
                  and the human /manager/* app are removed. Old auth/manager URLs
                  carry start-intent, so they redirect to /start (the action door:
                  the become-a-manager prompt) — not to /ask-your-agent (evaluate).
                  The front door is agent-native: authorize your agent at the
                  doorway (OAuth), and it self-admits via federation_apply. */}
              <Route
                path="/login"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/register"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/get-started"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/forgot-password"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/reset-password"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/verify-email"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/auth/github/callback"
                element={<Navigate to="/start" replace />}
              />
              <Route
                path="/manager/*"
                element={<Navigate to="/start" replace />}
              />

              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Zen and the Art of AI Maintenance — a book, served whole.
                Unlisted (investors-page mold: no nav entry, noindex, shared
                by URL) and outside the Layout shell on purpose: the book
                carries its own typography and doesn't wear portal chrome. */}
            <Route path="/zen" element={<ZenPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeModeProvider>
  );
}

export default App;
